import { useEffect, useState } from "react";
import { createPromoCode, updatePromoCode } from "../../api/promoCodes";
import { IPromoCode, ICreatePromoCodeDTO, IUpdatePromoCodeDTO } from "../../models/IPromoCode";
import { Alert, Button, Checkbox, DateInput, Dialog, Field, FormGrid, Input, Textarea } from "../../ui";

type PromoCodeDialogProps = {
  tournamentId: string;
  promoCode?: IPromoCode;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const PromoCodeDialog = (props: PromoCodeDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    code: "",
    discountPercentage: "",
    discountAmount: "",
    validFrom: "",
    validUntil: "",
    maxUsageCount: "",
    isActive: true,
    description: "",
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.promoCode) {
        setFormData({
          code: props.promoCode.code,
          discountPercentage: props.promoCode.discountPercentage?.toString() || "",
          discountAmount: props.promoCode.discountAmount?.toString() || "",
          validFrom: props.promoCode.validFrom ? props.promoCode.validFrom.split("T")[0] : "",
          validUntil: props.promoCode.validUntil ? props.promoCode.validUntil.split("T")[0] : "",
          maxUsageCount: props.promoCode.maxUsageCount?.toString() || "",
          isActive: props.promoCode.isActive,
          description: props.promoCode.description || "",
        });
      } else {
        setFormData({
          code: "",
          discountPercentage: "",
          discountAmount: "",
          validFrom: "",
          validUntil: "",
          maxUsageCount: "",
          isActive: true,
          description: "",
        });
      }
    }
  }, [props.isOpen, props.promoCode]);

  // The kit Dialog plays the closing animation itself
  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.code.trim()) {
      setErrorMessage("Promo code is required");
      return;
    }

    if (!formData.discountPercentage && !formData.discountAmount) {
      setErrorMessage("Either discount percentage or discount amount is required");
      return;
    }

    if (formData.discountPercentage && formData.discountAmount) {
      setErrorMessage("Please provide either discount percentage or discount amount, not both");
      return;
    }

    if (formData.discountPercentage && (parseFloat(formData.discountPercentage) <= 0 || parseFloat(formData.discountPercentage) > 100)) {
      setErrorMessage("Discount percentage must be between 0 and 100");
      return;
    }

    if (formData.discountAmount && parseFloat(formData.discountAmount) <= 0) {
      setErrorMessage("Discount amount must be greater than 0");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (props.promoCode) {
        // Update existing promo code
        const updateData: IUpdatePromoCodeDTO = {
          code: formData.code,
          discountPercentage: formData.discountPercentage ? parseFloat(formData.discountPercentage) : null,
          discountAmount: formData.discountAmount ? parseFloat(formData.discountAmount) : null,
          validFrom: formData.validFrom ? `${formData.validFrom}T00:00:00Z` : null,
          validUntil: formData.validUntil ? `${formData.validUntil}T23:59:59Z` : null,
          maxUsageCount: formData.maxUsageCount ? parseInt(formData.maxUsageCount) : null,
          isActive: formData.isActive,
          description: formData.description || null,
        };
        await updatePromoCode(props.promoCode.id, updateData);
      } else {
        // Create new promo code
        const createData: ICreatePromoCodeDTO = {
          code: formData.code,
          tournamentId: props.tournamentId,
          discountPercentage: formData.discountPercentage ? parseFloat(formData.discountPercentage) : null,
          discountAmount: formData.discountAmount ? parseFloat(formData.discountAmount) : null,
          validFrom: formData.validFrom ? `${formData.validFrom}T00:00:00Z` : null,
          validUntil: formData.validUntil ? `${formData.validUntil}T23:59:59Z` : null,
          maxUsageCount: formData.maxUsageCount ? parseInt(formData.maxUsageCount) : null,
          isActive: formData.isActive,
          description: formData.description || null,
        };
        await createPromoCode(createData);
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.promoCode ? "Edit Promo Code" : "Create Promo Code"}
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {props.promoCode ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <FormGrid columns={1}>
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        <Field label="Promo Code" required>
          {(fieldId) => (
            <Input
              id={fieldId}
              type="text"
              placeholder="e.g., SUMMER2024"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
              disabled={loading}
            />
          )}
        </Field>

        <FormGrid columns={2}>
          <Field label="Discount Percentage (%)">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 15"
                value={formData.discountPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange("discountPercentage", value);
                  if (value) handleChange("discountAmount", "");
                }}
                disabled={loading}
              />
            )}
          </Field>

          <Field label="Discount Amount">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 50"
                value={formData.discountAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange("discountAmount", value);
                  if (value) handleChange("discountPercentage", "");
                }}
                disabled={loading}
              />
            )}
          </Field>
        </FormGrid>

        <FormGrid columns={2}>
          <Field label="Valid From">
            {(fieldId) => (
              <DateInput
                id={fieldId}
                value={formData.validFrom}
                onChange={(e) => handleChange("validFrom", e.target.value)}
                disabled={loading}
                isClearable
              />
            )}
          </Field>

          <Field label="Valid Until">
            {(fieldId) => (
              <DateInput
                id={fieldId}
                value={formData.validUntil}
                onChange={(e) => handleChange("validUntil", e.target.value)}
                disabled={loading}
                isClearable
              />
            )}
          </Field>
        </FormGrid>

        <Field label="Max Usage Count">
          {(fieldId) => (
            <Input
              id={fieldId}
              type="number"
              min="0"
              placeholder="Leave empty for unlimited"
              value={formData.maxUsageCount}
              onChange={(e) => handleChange("maxUsageCount", e.target.value)}
              disabled={loading}
            />
          )}
        </Field>

        <Field label="Description">
          {(fieldId) => (
            <Textarea
              id={fieldId}
              placeholder="e.g., Summer promotional discount"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              disabled={loading}
            />
          )}
        </Field>

        <div>
          <Checkbox
            checked={formData.isActive}
            onChange={(checked) => handleChange("isActive", checked)}
            label="Active"
            disabled={loading}
          />
        </div>
      </FormGrid>
    </Dialog>
  );
};

export default PromoCodeDialog;

